import { Component, OnDestroy, OnInit, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AccessService, AppUser, Role, PagePermission, ChatAgentSubOption, UserCounts } from '../services/access.service';
import { Auth, onAuthStateChanged, Unsubscribe } from '@angular/fire/auth';
import { Observable, firstValueFrom, map } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { OwnerService } from '../services/owner.service';
import { AgentProfitService } from '../services/agent-profit.service';

@Component({
  selector: 'app-access-control',
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './access-control.component.html',
  styleUrl: './access-control.component.css'
})
export class AccessControlComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private accessService = inject(AccessService);
  private auth = inject(Auth);
  private ownerService = inject(OwnerService);
  private agentProfitService = inject(AgentProfitService);
  private authUnsubscribe?: Unsubscribe;

  userRole: AppUser['role'] | null = null;
  isLoading = true;
  isLoadingCounts = false;
  activeTab: 'page-access' | 'users' | 'roles' = 'page-access';
  userCounts: UserCounts = { total: 0, staff: 0, customers: 0 };

  // Data Observables
  users$?: Observable<AppUser[]>;
  roles$?: Observable<Role[]>;
  owners$?: Observable<any[]>;
  pagePermissions$?: Observable<PagePermission[]>;
  chatAgents$?: Observable<ChatAgentSubOption[]>;

  // Available Pages Definition (Root Pages only)
  availablePages = [
    { id: 'owner-kpi', name: 'Owner KPI (Dashboard)', icon: '📊' },
    { id: 'invoice', name: 'Invoices', icon: '📄' },
    { id: 'sales-invoice', name: 'Sales Invoice', icon: '🧾' },
    { id: 'profit-report', name: 'Profit Reports', icon: '💰' },
    { id: 'daily-report', name: 'Daily Report', icon: '📅' },
    { id: 'frame-report', name: 'Frame Report', icon: '🖼️' },
    { id: 'smrs-fee-report', name: 'SMRS Fee Report', icon: '📉' },
    { id: 'sales-report', name: 'Sales Report', icon: '💼' },
    { id: 'accounts', name: 'Chart of Accounts', icon: '🗂️' },
    { id: 'company-management', name: 'Company Management', icon: '👥' },
    { id: 'owner-management', name: 'Owner Management', icon: '👤' },
    { id: 'store-management', name: 'Store Management', icon: '🏪' },
    { id: 'smrs-rate-management', name: 'SMRS Rate Management', icon: '⚙️' },
    { id: 'bank-management', name: 'Bank Management', icon: '🏦' },
    { id: 'chat-agent', name: 'Chat Agent', icon: '🤖' },
    { id: 'agent-management', name: 'Agent Management', icon: '🛠️' },
    { id: 'artist-management', name: 'Artist Management', icon: '🎤' },
    { id: 'coupon-management', name: 'Coupon Management', icon: '🎟️' },
    { id: 'refund-management', name: 'Refund Review Queue', icon: '💳' },
    { id: 'artist-statement', name: 'Artist Statement', icon: '📝' },
    { id: 'dev-tool', name: 'Developer Tools', icon: '🛠️' },
    { id: 'access-control', name: 'Access Control', icon: '🔐' },
    { id: 'photobooth-management', name: 'Photobooth Management', icon: '📸' },
    { id: 'konobox-management', name: 'Konobox Management', icon: '🎤' }
  ];

  // Specific Action Sub-Options for Pages (e.g. Photobooth Management, Invoice, Coupon Management)
  pageActionSubOptions: Record<string, { id: string, name: string, icon?: string }[]> = {
    'invoice': [
      { id: 'invoice_delete-invoices', name: 'Delete Multiple Invoices', icon: '🗑️' }
    ],
    'coupon-management': [
      { id: 'coupon-management_create-coupons', name: 'Create Coupons (Single & Bulk)', icon: '➕' }
    ],
    'photobooth-management': [
      { id: 'photobooth-management_restart-all', name: 'Restart All Machines', icon: '🔄' },
      { id: 'photobooth-management_release-all', name: 'Release App to All Machines', icon: '🚀' },
      { id: 'photobooth-management_release-device', name: 'Release App to Device', icon: '📲' }
    ],
    'konobox-management': [
      { id: 'konobox-management_release-device', name: 'Release App to Device', icon: '📲' },
      { id: 'konobox-management_release-all', name: 'Release App to All Machines', icon: '🚀' },
      { id: 'konobox-management_remote-control', name: 'Remote Control', icon: '🎮' },
      { id: 'konobox-management_release-delete', name: 'Delete Release', icon: '🗑️' }
    ]
  };

  // Pages that support the "Show all companies" sub-option
  pagesWithCompanyFilterSubOption = new Set<string>([
    'profit-report',
    'daily-report',
    'invoice',
    'smrs-fee-report',
    'sales-report',
    'owner-kpi'
  ]);

  expandedPages = new Set<string>();

  hasChildSettings(pageId: string): boolean {
    if (pageId === 'chat-agent') return true;
    return this.pagesWithCompanyFilterSubOption.has(pageId) || !!(this.pageActionSubOptions[pageId]?.length);
  }

  togglePageExpand(pageId: string) {
    if (this.expandedPages.has(pageId)) {
      this.expandedPages.delete(pageId);
    } else {
      this.expandedPages.add(pageId);
    }
  }

  openChatAgent() {
    this.agentProfitService.openChat();
  }

  // Form Models
  newRole: Role = { name: '', description: '', color: '#3b82f6', isExternal: false, isCustomer: false };
  editingRole: Role | null = null;
  isAddingRole = false;
  isEditingRole = false;
  isSavingRole = false;
  roleErrorMessage: string | null = null;

  newUser: AppUser = { email: '', displayName: '', role: 'user', ownerIds: [] };
  editingUser: AppUser | null = null;
  isAddingUser = false;

  rolesList: Role[] = [];
  filterRolesList: Role[] = [];

  get assignableRoles(): Role[] {
    return this.rolesList.filter(r => r.name.toLowerCase() !== 'superadmin');
  }

  // Filtering & Pagination
  searchEmail = '';
  showRoleFilterDropdown = false;
  selectedRoles: Set<string> = new Set();
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [10, 20, 50, 100];
  totalPages = 1;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-dropdown')) {
      this.showRoleFilterDropdown = false;
    }
  }

  toggleRoleFilterDropdown() {
    this.showRoleFilterDropdown = !this.showRoleFilterDropdown;
  }

  get isAllRolesSelected(): boolean {
    return this.filterRolesList.length > 0 && this.selectedRoles.size === this.filterRolesList.length;
  }

  toggleAllRolesFilter() {
    if (this.isAllRolesSelected) {
      this.selectedRoles.clear();
    } else {
      this.filterRolesList.forEach(r => this.selectedRoles.add(r.name));
    }
    this.currentPage = 1;
  }

  isRoleFilterSelected(roleName: string): boolean {
    if (!roleName) return false;
    const lower = roleName.toLowerCase().trim();
    for (const r of this.selectedRoles) {
      if (r.toLowerCase().trim() === lower) return true;
    }
    return false;
  }

  toggleRoleFilterItem(roleName: string) {
    const lower = roleName.toLowerCase().trim();
    let foundKey: string | null = null;
    for (const r of this.selectedRoles) {
      if (r.toLowerCase().trim() === lower) {
        foundKey = r;
        break;
      }
    }
    if (foundKey) {
      this.selectedRoles.delete(foundKey);
    } else {
      this.selectedRoles.add(roleName);
    }
    this.currentPage = 1;
  }

  searchedUsers: AppUser[] = [];
  searchDebounceTimer: any = null;
  isSearching = false;

  onSearchChange() {
    this.currentPage = 1;
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    const search = (this.searchEmail || '').trim().toLowerCase();
    if (search.length >= 2) {
      this.searchDebounceTimer = setTimeout(async () => {
        this.isSearching = true;
        try {
          this.searchedUsers = await this.accessService.searchUsersByEmail(search);
        } catch (e) {
          console.error('Error searching users:', e);
          this.searchedUsers = [];
        } finally {
          this.isSearching = false;
        }
      }, 300);
    } else {
      this.searchedUsers = [];
      this.isSearching = false;
    }
  }

  clearSearch() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchEmail = '';
    this.searchedUsers = [];
    this.isSearching = false;
    this.currentPage = 1;
  }

  getFilteredUsers(users: AppUser[], roles: Role[]): AppUser[] {
    const search = (this.searchEmail || '').trim().toLowerCase();
    const allUsersMap = new Map<string, AppUser>();
    (users || []).forEach(u => allUsersMap.set(u.uid || u.email || '', u));
    (this.searchedUsers || []).forEach(u => {
      const key = u.uid || u.email || '';
      if (key && !allUsersMap.has(key)) {
        allUsersMap.set(key, u);
      }
    });

    const combinedUsers = Array.from(allUsersMap.values());
    return combinedUsers.filter(u => {
      const roleName = this.getRoleName(u, roles);
      const isSearchedDirectly = this.searchedUsers.some(su => (su.uid && su.uid === u.uid) || (su.email && su.email === u.email));
      const matchesRole = isSearchedDirectly || this.isRoleFilterSelected(roleName);
      const userEmail = (u.email || '').toLowerCase();
      const userName = (u.displayName || (u as any).name || '').toLowerCase();
      const matchesSearch = !search || userEmail.includes(search) || userName.includes(search);
      return matchesRole && matchesSearch;
    });
  }

  getPaginatedUsers(users: AppUser[], roles: Role[]): AppUser[] {
    const filtered = this.getFilteredUsers(users, roles);
    this.totalPages = Math.ceil(filtered.length / this.pageSize) || 1;
    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return filtered.slice(startIndex, startIndex + this.pageSize);
  }

  nextPage() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  prevPage() {
    if (this.currentPage > 1) this.currentPage--;
  }

  onPageSizeChange() {
    this.currentPage = 1;
  }

  ngOnInit() {
    this.authUnsubscribe = onAuthStateChanged(this.auth, async (user) => {
      this.isLoading = true;
      if (user) {
        const profile$ = this.accessService.getUserProfile(user.uid);
        const profile = await firstValueFrom(profile$);
        this.userRole = profile?.role || null;
        this.users$ = this.accessService.getUsers();
        this.owners$ = this.ownerService.getOwners();
        this.roles$ = this.accessService.getRoles().pipe(
          map(roles => {
            const customSorted = [...roles].sort((a, b) => {
              if (a.name.toLowerCase() === 'admin') return -1;
              if (b.name.toLowerCase() === 'admin') return 1;
              return a.name.localeCompare(b.name);
            });
            this.rolesList = customSorted.filter(r => r.name.toLowerCase() !== 'superadmin');
            
            // Tab 2 filter list includes superadmin at the top for user filtering
            this.filterRolesList = [
              { name: 'superadmin', description: 'System Superadmin' },
              ...this.rolesList
            ];

            // Initialize selectedRoles with staff/admin/superadmin roles (excluding 'user') on first load
            if (this.selectedRoles.size === 0 && this.filterRolesList.length > 0) {
              this.filterRolesList
                .filter(r => r.name.toLowerCase() !== 'user')
                .forEach(r => this.selectedRoles.add(r.name));
            }
            return this.rolesList;
          })
        );
        this.pagePermissions$ = this.accessService.getPagePermissions();
        this.chatAgents$ = this.accessService.getChatAgents();
        this.loadUserCounts();
      } else {
        this.userRole = null;
      }
      this.isLoading = false;
    });
  }

  async loadUserCounts() {
    this.isLoadingCounts = true;
    try {
      this.userCounts = await this.accessService.getUserCounts();
    } catch (e) {
      console.error('Failed to load user counts:', e);
    } finally {
      this.isLoadingCounts = false;
    }
  }

  switchTab(tab: 'page-access' | 'users' | 'roles') {
    this.activeTab = tab;
    if (tab === 'users') {
      this.loadUserCounts();
    }
  }

  ngOnDestroy() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
  }

  formatDate(val: any): string {
    if (!val) return '-';
    let date: Date;
    if (val?.toDate && typeof val.toDate === 'function') {
      date = val.toDate();
    } else if (val instanceof Date) {
      date = val;
    } else if (typeof val === 'string' || typeof val === 'number') {
      date = new Date(val);
    } else if (val?.seconds) {
      date = new Date(val.seconds * 1000);
    } else {
      return '-';
    }
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // === Role Actions & Modals ===
  openAddRoleModal() {
    this.roleErrorMessage = null;
    this.isAddingRole = true;
    const randomColor = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    this.newRole = { name: '', description: '', color: randomColor, isExternal: false, isCustomer: false };
  }

  closeAddRoleModal() {
    this.roleErrorMessage = null;
    this.isAddingRole = false;
  }

  openEditRoleModal(role: Role) {
    this.roleErrorMessage = null;
    this.editingRole = {
      id: role.id,
      name: role.name,
      description: role.description || '',
      color: (role as any).color || this.getRoleColor(role.name, null),
      isExternal: !!role.isExternal,
      isCustomer: !!role.isCustomer
    };
    this.isEditingRole = true;
  }

  closeEditRoleModal() {
    this.roleErrorMessage = null;
    this.isEditingRole = false;
    this.editingRole = null;
  }

  async addRole() {
    this.roleErrorMessage = null;
    const roleName = (this.newRole.name || '').trim();
    if (!roleName) {
      this.roleErrorMessage = 'Role name is required.';
      return;
    }

    if (this.rolesList.some(r => r.name.toLowerCase().trim() === roleName.toLowerCase())) {
      this.roleErrorMessage = `A role named "${roleName}" already exists.`;
      return;
    }

    this.isSavingRole = true;
    try {
      await this.accessService.addRole({
        name: roleName,
        description: this.newRole.description?.trim() || '',
        color: this.newRole.color || '#3b82f6',
        isExternal: !this.newRole.isCustomer && !!this.newRole.isExternal,
        isCustomer: !!this.newRole.isCustomer
      });
      this.closeAddRoleModal();
    } catch (err: any) {
      console.error('Error adding role:', err);
      this.roleErrorMessage = err?.message || 'Failed to add role.';
    } finally {
      this.isSavingRole = false;
    }
  }

  async saveRole() {
    if (!this.editingRole || !this.editingRole.id) return;
    this.roleErrorMessage = null;
    const roleName = (this.editingRole.name || '').trim();
    if (!roleName) {
      this.roleErrorMessage = 'Role name is required.';
      return;
    }

    // Check duplicate name across other roles
    if (this.rolesList.some(r => r.id !== this.editingRole!.id && r.name.toLowerCase().trim() === roleName.toLowerCase())) {
      this.roleErrorMessage = `A role named "${roleName}" already exists.`;
      return;
    }

    this.isSavingRole = true;
    try {
      await this.accessService.updateRole({
        id: this.editingRole.id,
        name: roleName,
        description: this.editingRole.description?.trim() || '',
        color: this.editingRole.color || '#3b82f6',
        isExternal: !this.editingRole.isCustomer && !!this.editingRole.isExternal,
        isCustomer: !!this.editingRole.isCustomer
      });
      this.closeEditRoleModal();
    } catch (err: any) {
      console.error('Error updating role:', err);
      this.roleErrorMessage = err?.message || 'Failed to update role.';
    } finally {
      this.isSavingRole = false;
    }
  }

  async deleteRole(id?: string) {
    if (id && confirm('Are you sure you want to delete this role?')) {
      await this.accessService.deleteRole(id);
    }
  }

  addUserErrorMessage: string | null = null;
  isSavingUser = false;

  // === User Actions ===
  async addUser() {
    this.addUserErrorMessage = null;
    const normalizedEmail = (this.newUser.email || '').trim().toLowerCase();
    if (!normalizedEmail) {
      this.addUserErrorMessage = 'Please enter a valid email address.';
      return;
    }

    this.isSavingUser = true;
    try {
      if (this.newUser.role && this.rolesList) {
        const found = this.rolesList.find(r => r.name.toLowerCase() === this.newUser.role!.toLowerCase());
        if (found && found.id) {
          this.newUser.roleId = found.id;
          this.newUser.role = found.name;
        }
      }
      this.newUser.email = normalizedEmail;
      await this.accessService.addUser(this.newUser);
      this.newUser = { email: '', displayName: '', role: 'User', ownerIds: [] }; // Reset form
      this.isAddingUser = false;
    } catch (err: any) {
      console.error('Error adding user:', err);
      this.addUserErrorMessage = err?.message || 'A user with this email address already exists.';
    } finally {
      this.isSavingUser = false;
    }
  }

  openAddUserModal() {
    this.addUserErrorMessage = null;
    this.isAddingUser = true;
    const defaultUserRole = this.rolesList.find(r => r.name.toLowerCase() === 'user');
    this.newUser = {
      email: '',
      displayName: '',
      role: defaultUserRole ? defaultUserRole.name : 'User',
      roleId: defaultUserRole ? defaultUserRole.id : undefined,
      ownerIds: []
    };
  }

  closeAddUserModal() {
    this.addUserErrorMessage = null;
    this.isAddingUser = false;
  }

  openEditUserModal(user: AppUser) {
    this.editingUser = { ...user };
    const found = this.getResolvedRole(user, this.rolesList);
    if (found) {
      this.editingUser.role = found.name;
      this.editingUser.roleId = found.id;
    } else if (!this.editingUser.role) {
      const defaultUserRole = this.rolesList.find(r => r.name.toLowerCase() === 'user');
      if (defaultUserRole) {
        this.editingUser.role = defaultUserRole.name;
        this.editingUser.roleId = defaultUserRole.id;
      } else {
        this.editingUser.role = 'User';
      }
    }

    if (!this.editingUser.ownerIds) {
      this.editingUser.ownerIds = [];
      if ((user as any).ownerId) {
        this.editingUser.ownerIds.push((user as any).ownerId);
      }
    }
  }

  closeEditUserModal() {
    this.editingUser = null;
  }

  async saveUser() {
    if (this.editingUser && this.editingUser.uid) {
      if (this.editingUser.role && this.rolesList) {
        const found = this.rolesList.find(r => r.name.toLowerCase() === this.editingUser!.role!.toLowerCase());
        if (found && found.id) {
          this.editingUser.roleId = found.id;
          this.editingUser.role = found.name;
        }
      }
      await this.accessService.updateUser(this.editingUser);
      this.editingUser = null;
    }
  }

  async deleteUser(uid?: string) {
    if (uid && confirm('Are you sure you want to delete this user?')) {
      await this.accessService.deleteUser(uid);
    }
  }

  // === Page Access Actions ===
  isPageCheckboxDisabled(pageId: string, role: Role): boolean {
    if (!role) return false;
    const norm = role.name.toLowerCase().trim();
    if (norm === 'superadmin') return true;
    if (norm === 'admin' && this.userRole !== 'superadmin') return true;
    if (role.isCustomer) return true;
    if (role.isExternal) {
      return pageId !== 'profit-report' && pageId !== 'daily-report';
    }
    return false;
  }

  async togglePagePermission(pageId: string, pageName: string, role: string, event: Event, currentPermissions: PagePermission[]) {
    const normRole = (role || '').toLowerCase().trim();
    const roleObj = this.rolesList.find(r => r.name.toLowerCase().trim() === normRole);
    if (roleObj?.isCustomer) {
      const checkbox = event.target as HTMLInputElement;
      if (checkbox) checkbox.checked = false;
      return;
    }

    if (roleObj?.isExternal && pageId !== 'profit-report' && pageId !== 'daily-report') {
      const checkbox = event.target as HTMLInputElement;
      if (checkbox) checkbox.checked = false;
      return;
    }

    const isChecked = (event.target as HTMLInputElement).checked;
    let perm = currentPermissions.find(p => p.id === pageId);

    let roles = perm?.roles ? [...perm.roles] : [];
    let showAllCompaniesRoles = perm?.showAllCompaniesRoles ? [...perm.showAllCompaniesRoles] : [];

    if (isChecked) {
      if (!roles.some(r => r.toLowerCase().trim() === normRole)) {
        roles.push(role);
      }
      // By default, when a page is checked for a non-external role, "Show all companies" is checked as well
      if (!roleObj?.isExternal && !showAllCompaniesRoles.some(r => r.toLowerCase().trim() === normRole)) {
        showAllCompaniesRoles.push(role);
      }
    } else {
      roles = roles.filter(r => r.toLowerCase().trim() !== normRole);
      showAllCompaniesRoles = showAllCompaniesRoles.filter(r => r.toLowerCase().trim() !== normRole);
    }

    const updatedPerm: PagePermission = {
      id: pageId,
      name: pageName,
      roles: roles,
      showAllCompaniesRoles: showAllCompaniesRoles
    };

    await this.accessService.updatePagePermission(updatedPerm);

    // If toggling the parent chat-agent permission:
    if (pageId === 'chat-agent') {
      const childPerms = currentPermissions.filter(p => p.id && p.id.startsWith('chat-agent_'));
      if (!isChecked) {
        // Cascade uncheck: Remove role from all chat-agent_* sub-permissions
        for (const cp of childPerms) {
          if (cp.roles && cp.roles.some(r => r.toLowerCase().trim() === normRole)) {
            const updatedChildRoles = cp.roles.filter(r => r.toLowerCase().trim() !== normRole);
            await this.accessService.updatePagePermission({
              ...cp,
              roles: updatedChildRoles
            });
          }
        }
      } else {
        // Cascade check: Enable all agents for this role by default if none are checked
        try {
          if (this.chatAgents$) {
            const agents: ChatAgentSubOption[] = await firstValueFrom(this.chatAgents$);
            if (agents && agents.length > 0) {
              for (const ag of agents) {
                const existing = currentPermissions.find(p => p.id === ag.subOptionId);
                const curRoles = existing?.roles ? [...existing.roles] : [];
                if (!curRoles.some(r => r.toLowerCase().trim() === normRole)) {
                  curRoles.push(role);
                  await this.accessService.updatePagePermission({
                    id: ag.subOptionId,
                    name: ag.name,
                    roles: curRoles
                  });
                }
              }
            }
          }
        } catch (err) {
          console.warn('Failed to cascade enable chat agents for role:', err);
        }
      }
    }
  }

  async toggleShowAllCompanies(pageId: string, pageName: string, role: string, event: Event, currentPermissions: PagePermission[]) {
    const normRole = (role || '').toLowerCase().trim();
    if (normRole === 'superadmin') {
      return; // Superadmin always has show all companies
    }

    const roleObj = this.rolesList.find(r => r.name.toLowerCase().trim() === normRole);
    if (roleObj?.isCustomer || roleObj?.isExternal) {
      const checkbox = event.target as HTMLInputElement;
      if (checkbox) checkbox.checked = false;
      return;
    }

    const isChecked = (event.target as HTMLInputElement).checked;
    let perm = currentPermissions.find(p => p.id === pageId);

    const roles = perm?.roles ? [...perm.roles] : [];
    let showAllCompaniesRoles = perm?.showAllCompaniesRoles ? [...perm.showAllCompaniesRoles] : [];

    if (isChecked) {
      if (!showAllCompaniesRoles.some(r => r.toLowerCase().trim() === normRole)) {
        showAllCompaniesRoles.push(role);
      }
    } else {
      showAllCompaniesRoles = showAllCompaniesRoles.filter(r => r.toLowerCase().trim() !== normRole);
    }

    const updatedPerm: PagePermission = {
      id: pageId,
      name: pageName,
      roles: roles,
      showAllCompaniesRoles: showAllCompaniesRoles
    };

    await this.accessService.updatePagePermission(updatedPerm);
  }

  isRoleAllowed(pageId: string, role: string, permissions: PagePermission[]): boolean {
    if (!role || !permissions) return false;
    const normalizedRole = role.toLowerCase().trim();
    if (normalizedRole === 'superadmin') return true;

    const roleObj = this.rolesList.find(r => r.name.toLowerCase().trim() === normalizedRole);
    if (roleObj?.isCustomer) return false;

    if (pageId === 'chat-agent') {
      const parentPerm = permissions.find(p => p.id === 'chat-agent');
      if (!parentPerm || !parentPerm.roles || !parentPerm.roles.some(r => r.toLowerCase().trim() === normalizedRole)) {
        return false;
      }
      const childPerms = permissions.filter(p => p.id && p.id.startsWith('chat-agent_'));
      if (childPerms.length > 0) {
        return childPerms.some(p => p.roles && p.roles.some(r => r.toLowerCase().trim() === normalizedRole));
      }
      return true;
    }

    const perm = permissions.find(p => p.id === pageId);
    return perm ? perm.roles.some(r => r.toLowerCase().trim() === normalizedRole) : false;
  }

  isAgentSubOptionAllowed(subOptionId: string, role: string, permissions: PagePermission[]): boolean {
    if (!role || !permissions) return false;
    const normalizedRole = role.toLowerCase().trim();
    if (normalizedRole === 'superadmin') return true;

    const roleObj = this.rolesList.find(r => r.name.toLowerCase().trim() === normalizedRole);
    if (roleObj?.isCustomer) return false;

    // 1. Parent chat-agent must be allowed
    const parentPerm = permissions.find(p => p.id === 'chat-agent');
    if (!parentPerm || !parentPerm.roles || !parentPerm.roles.some(r => r.toLowerCase().trim() === normalizedRole)) {
      return false;
    }

    // 2. Sub-option permission check
    const perm = permissions.find(p => p.id === subOptionId);
    if (perm && perm.roles) {
      return perm.roles.some(r => r.toLowerCase().trim() === normalizedRole);
    }

    // Fallback to parent permission if sub-option document doesn't exist yet
    return true;
  }

  isShowAllCompaniesAllowed(pageId: string, role: string, permissions: PagePermission[]): boolean {
    const normRole = (role || '').toLowerCase().trim();
    if (normRole === 'superadmin') return true;

    const roleObj = this.rolesList.find(r => r.name.toLowerCase().trim() === normRole);
    if (roleObj?.isCustomer || roleObj?.isExternal) return false;

    const perm = permissions.find(p => p.id === pageId);
    if (!perm) return false;
    // If not in parent roles list, sub-option is not allowed
    if (!perm.roles.some(r => r.toLowerCase().trim() === normRole)) return false;
    // If showAllCompaniesRoles is undefined (legacy), default to true
    if (!perm.showAllCompaniesRoles) return true;
    return perm.showAllCompaniesRoles.some(r => r.toLowerCase().trim() === normRole);
  }

  isOwnerSelected(user: AppUser, ownerId?: string): boolean {
    if (!ownerId) return false;
    return user.ownerIds?.includes(ownerId) ?? false;
  }

  toggleOwnerSelection(user: AppUser, ownerId: string | undefined, event: Event) {
    if (!ownerId) return;
    const isChecked = (event.target as HTMLInputElement).checked;
    if (!user.ownerIds) {
      user.ownerIds = [];
    }
    if (isChecked) {
      if (!user.ownerIds.includes(ownerId)) {
        user.ownerIds.push(ownerId);
      }
    } else {
      user.ownerIds = user.ownerIds.filter(id => id !== ownerId);
    }
  }

  getDisplayOwners(ownerIds: string[] | undefined, allOwners: any[] | null): string {
    if (!ownerIds || !ownerIds.length || !allOwners) return '-';

    const selectedOwners = allOwners.filter(o => ownerIds.includes(o.id));
    if (selectedOwners.length === 0) return '-';

    const names = selectedOwners.map(o => o.name);
    if (names.length <= 2) {
      return names.join(', ');
    }
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }

  getRoleColor(roleName: string, roles: any[] | null | undefined): string | null {
    if (!roleName) return '#777777';
    if (roles) {
      const found = roles.find(r => r.name?.toLowerCase() === roleName.toLowerCase());
      if (found && found.color) {
        return found.color;
      }
    }
    const builtIns: Record<string, string> = {
      superadmin: '#32BA42',
      admin: '#d9534f',
      manager: '#f0ad4e',
      user: '#5bc0de',
      guest: '#777777'
    };
    const normalized = roleName.toLowerCase();
    return builtIns[normalized] || '#5bc0de';
  }

  // === Role Resolution Helpers ===
  getResolvedRole(user: AppUser | string | null | undefined, roles: Role[] | null | undefined): Role | undefined {
    const list = (roles && roles.length) ? roles : this.rolesList;
    if (!user || !list || !list.length) return undefined;

    let roleStr: string | undefined;
    let roleId: string | undefined;

    if (typeof user === 'string') {
      roleStr = user;
    } else {
      roleStr = user.role;
      roleId = user.roleId || (user as any).role_id;
    }

    return list.find(r =>
      (r.id && (r.id === roleId || r.id === roleStr)) ||
      (r.name && roleStr && r.name.toLowerCase() === roleStr.toLowerCase())
    );
  }

  getRoleName(user: AppUser | null | undefined, roles: Role[] | null | undefined): string {
    if (!user) return 'Guest';
    const found = this.getResolvedRole(user, roles);
    if (found) return found.name;
    if (user.role && !user.role.startsWith('http') && user.role.length < 30) {
      return user.role;
    }
    return 'User';
  }

  getRoleColorForUser(user: AppUser | string | null | undefined, roles: Role[] | null | undefined): string {
    const roleName = typeof user === 'string' ? user : this.getRoleName(user as AppUser, roles);
    const foundRole = this.getResolvedRole(user, roles);

    if (foundRole && (foundRole as any).color) {
      return (foundRole as any).color;
    }

    const builtIns: Record<string, string> = {
      superadmin: '#32BA42',
      admin: '#d9534f',
      manager: '#f0ad4e',
      user: '#5bc0de',
      guest: '#777777'
    };

    const normalized = roleName.toLowerCase();
    return builtIns[normalized] || '#5bc0de';
  }
}
